import {useOnEvent} from 'react-app-events'
import cLogger from '@helpers/logger/conditionalLogger'
import isScrapping from '@helpers/misc/isScrapping'
import isServerSide from '@helpers/misc/isServerSide'
import useCookiesAccepted from '@hooks/useCookiesAccepted'
import getEnv from '@providers/getEnv'

import useAmplitudeImplementation, {ANALYTICS_AMPLITUDE} from './implementations/amplitude'
import useGA4Implementation from './implementations/google/ga4'
import useGTMImplementation from './implementations/google/gtm'
import useMetaImplementation from './implementations/meta'
import {AnalyticsServicesController} from './types/AnalyticsServicesTypes'

export const ANALYTICS_V2_FORCE = false // Set force to true to run in local
export const ANALYTICS_V2_VERBOSE = false // Set verbose to true to debug
export function AnalyticsV2Controller(): AnalyticsServicesController {
  const v = ANALYTICS_V2_VERBOSE
  const amplitude = useAmplitudeImplementation({verbose: v})
  const meta = useMetaImplementation({verbose: v})
  const ga4 = useGA4Implementation({verbose: v})
  const gtm = useGTMImplementation({verbose: v})
  const implementations = Object.fromEntries(
    Object.entries({
      [amplitude.name]: amplitude,
      [meta.name]: meta,
      [ga4.name]: ga4,
      [gtm.name]: gtm
    }).filter(([, implementation]) => implementation.active)
  )
  const env = getEnv()
  const enabled =
    (useCookiesAccepted() &&
      !isServerSide() &&
      !isScrapping &&
      ['prod', 'dev'].includes(env) &&
      Object.keys(implementations).length > 0) ||
    ANALYTICS_V2_FORCE

  async function initAll(): Promise<void> {
    if (!enabled) return
    cLogger(v).info(`Initializing analytics V2 for all`)
    const initPromises = Object.values(implementations).map(async implementation => {
      try {
        implementation.init()
        cLogger(v).info(`${implementation.name} initialized`)
      } catch (error) {
        cLogger(v).error(`Error initializing ${implementation.name}`, error)
      }
    })
    await Promise.all(initPromises)
  }

  async function setUserIdAll(userId: string): Promise<void> {
    if (!enabled || !userId) return
    cLogger(v).info(`Setting up for all userId ${userId}`)
    // Only available for Amplitude (so far)
    try {
      await amplitude.setUserId(userId)
    } catch (err) {
      cLogger(v).warn(`Error setting setUserId on ${ANALYTICS_AMPLITUDE}`, err)
    }
  }

  async function trackEventOnAll(
    eventName: string,
    properties: Record<string, any>
  ): Promise<void> {
    if (!enabled) return
    cLogger(v).info(`Tracking ${eventName} for all`, properties)
    for (const implementation of Object.values(implementations)) {
      await trackEventOn(implementation.name, eventName, properties)
    }
  }

  async function trackEventOn(
    implName: string,
    eventName: string,
    properties: Record<string, any>
  ): Promise<void> {
    if (!enabled) return
    try {
      await implementations[implName][`track${eventName}`](properties)
      cLogger(v).info(`Tracked event ${eventName} on ${implementations[implName].name}`)
    } catch (err) {
      cLogger(v).warn(`Error tracking event ${eventName} for ${implName}`, err)
    }
  }
  async function trackEventAsDefaultOn(
    implName: string,
    eventName: string,
    properties: Record<string, any>
  ): Promise<void> {
    if (!enabled) return
    try {
      await implementations[implName].trackEventAsDefault(eventName, properties)
      cLogger(v).info(`Sending event ${eventName} as default on ${implementations[implName].name}`)
    } catch (err) {
      cLogger(v).warn(`Error tracking event ${eventName} as default for ${implName}`, err)
    }
  }

  async function trackProductViewOnAll(product: any, extraProperties?: any) {
    if (!product?.availabilityAt) return
    await trackEventOnAll('ProductView', {...product, extraProperties})
  }

  async function trackAddToCartOnAll(product: any, extraProperties?: any) {
    if (!product?.availabilityAt) return
    await trackEventOnAll('AddToCart', {...product, extraProperties})
  }

  async function trackInitCheckoutOnAll(preferences: any, extraProperties?: any) {
    await trackEventOnAll('InitCheckout', {...preferences, extraProperties})
  }

  async function trackPurchaseOnAll(order: any, extraProperties?: any) {
    if (!order) return
    await trackEventOnAll('Purchase', {...order, extraProperties})
  }

  useOnEvent('updateAnalyticsUserId', async (newUserId: string) => {
    await setUserIdAll(newUserId)
  })

  return {
    implementations: implementations,
    enabled,
    initAll,
    setUserIdAll,
    trackEventOn,
    trackEventAsDefaultOn,
    trackEventOnAll,
    trackScreenViewAll: async () => {}, //Tracking each page is complex, it has a different default behaviour by implementation. See each implementation for dtails.
    trackProductViewOnAll,
    trackAddToCartOnAll,
    trackInitCheckoutOnAll,
    trackPurchaseOnAll
  }
}
